import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  DollarSign, Users, Building2, TrendingUp, Download, Filter,
  CheckCircle2, Clock, ArrowRight, AlertCircle, CreditCard,
  ChevronDown, ChevronUp, Search, RefreshCw, Shield, Loader2,
  Handshake, FileText, ExternalLink, Repeat, Mail, Send, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800', icon: Clock },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800', icon: AlertCircle },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
};

const DISBURSEMENT_CONFIG = {
  awaiting_payment: { label: 'Awaiting Payment', color: 'bg-gray-100 text-gray-600' },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700' },
  disbursed: { label: 'Disbursed', color: 'bg-emerald-100 text-emerald-700' },
};

const StatCard = ({ icon: Icon, label, value, subtext, gradient }) => (
  <div className={`rounded-xl p-5 bg-gradient-to-br ${gradient} text-white`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <p className="text-2xl font-black">{value}</p>
    <p className="text-sm font-medium text-white/80">{label}</p>
    {subtext && <p className="text-xs text-white/60 mt-1">{subtext}</p>}
  </div>
);

const DonationsView = ({ businessProfile }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [donations, setDonations] = useState([]);
  const [obligations, setObligations] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [donationPage, setDonationPage] = useState(1);
  const [donationTotal, setDonationTotal] = useState(0);
  const [donationPages, setDonationPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDonorType, setFilterDonorType] = useState('');
  const [searchCharity, setSearchCharity] = useState('');
  const [expandedObligation, setExpandedObligation] = useState(null);
  const [settlingCharity, setSettlingCharity] = useState(null);
  const [settlementProcessing, setSettlementProcessing] = useState(false);
  const [pendingSettlementId, setPendingSettlementId] = useState(null);
  const [settlementError, setSettlementError] = useState('');
  const [staleDisbursements, setStaleDisbursements] = useState(null);
  const [unclaimedCharities, setUnclaimedCharities] = useState([]);
  const [confirmingDisbursement, setConfirmingDisbursement] = useState(false);
  const [recurring, setRecurring] = useState(null);
  const [outreach, setOutreach] = useState([]);
  const [claimTarget, setClaimTarget] = useState(null);
  const [claimEmail, setClaimEmail] = useState('');
  const [claimContact, setClaimContact] = useState('');
  const [sendingClaim, setSendingClaim] = useState(false);
  const [claimError, setClaimError] = useState('');

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/donations/admin/overview`, { withCredentials: true });
      setOverview(res.data);
    } catch (err) {
      console.error('Failed to fetch overview:', err);
    }
  }, []);

  const fetchDonations = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (filterStatus) params.append('status', filterStatus);
      if (filterDonorType) params.append('donor_type', filterDonorType);
      if (searchCharity) params.append('charity_name', searchCharity);

      const res = await axios.get(`${BACKEND_URL}/api/donations/admin/list?${params}`, { withCredentials: true });
      setDonations(res.data.donations);
      setDonationTotal(res.data.total);
      setDonationPages(res.data.pages);
      setDonationPage(page);
    } catch (err) {
      console.error('Failed to fetch donations:', err);
    }
  }, [filterStatus, filterDonorType, searchCharity]);

  const fetchObligations = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/donations/admin/match-obligations`, { withCredentials: true });
      setObligations(res.data);
    } catch (err) {
      console.error('Failed to fetch obligations:', err);
    }
  }, []);

  const fetchSettlements = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/donations/admin/settlements`, { withCredentials: true });
      setSettlements(res.data.settlements);
    } catch (err) {
      console.error('Failed to fetch settlements:', err);
    }
  }, []);

  const fetchNudges = useCallback(async () => {
    try {
      const [stale, claims, sent] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/donations/admin/stale-disbursements`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/everyorg/charity-claim-status`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/everyorg/claim-outreach`, { withCredentials: true }),
      ]);
      setStaleDisbursements(stale.data);
      setUnclaimedCharities(claims.data.unclaimed || []);
      setOutreach(sent.data.outreach || []);
    } catch (err) {
      console.error('Failed to fetch disbursement nudges:', err);
    }
  }, []);

  const fetchRecurring = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/donations/admin/recurring`, { withCredentials: true });
      setRecurring(res.data);
    } catch (err) {
      console.error('Failed to fetch recurring givers:', err);
    }
  }, []);

  const sendClaimInvite = async (e) => {
    e.preventDefault();
    setSendingClaim(true);
    setClaimError('');
    try {
      await axios.post(`${BACKEND_URL}/api/everyorg/claim-outreach`, {
        charity_ein: claimTarget.charity_ein,
        charity_name: claimTarget.charity_name,
        to_email: claimEmail.trim(),
        contact_name: claimContact.trim(),
      }, { withCredentials: true });
      setClaimTarget(null);
      setClaimEmail('');
      setClaimContact('');
      await fetchNudges();
    } catch (err) {
      setClaimError(err.response?.data?.detail || 'Could not send the invite');
    } finally {
      setSendingClaim(false);
    }
  };

  const confirmStaleDisbursements = async () => {
    if (!staleDisbursements?.donations?.length) return;
    setConfirmingDisbursement(true);
    try {
      await axios.post(`${BACKEND_URL}/api/donations/admin/bulk-disbursement`, {
        donation_ids: staleDisbursements.donations.map(d => d.donation_id),
        reference: `Confirmed ${new Date().toLocaleDateString()}`,
      }, { withCredentials: true });
      await Promise.all([fetchNudges(), fetchDonations(), fetchOverview()]);
    } catch (err) {
      console.error('Bulk disbursement failed:', err);
    } finally {
      setConfirmingDisbursement(false);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchOverview(), fetchDonations(), fetchObligations(), fetchSettlements(),
        fetchNudges(), fetchRecurring()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchOverview, fetchDonations, fetchObligations, fetchSettlements, fetchNudges, fetchRecurring]);

  useEffect(() => {
    if (!loading) fetchDonations(1);
  }, [filterStatus, filterDonorType, searchCharity]);

  const handleExport = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/donations/admin/export`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `donations_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleSettleMatch = async (obligation) => {
    setSettlingCharity(obligation.charity_name);
    setSettlementProcessing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/everyorg/match-settlement`, {
        charity_name: obligation.charity_name,
        charity_ein: obligation.charity_ein,
        amount: obligation.balance_due,
        return_origin: window.location.origin,
      }, { withCredentials: true });

      // Money goes straight from the company to Every.org — we only record it once their webhook confirms
      window.open(res.data.donate_url, '_blank', 'noopener');
      setPendingSettlementId(res.data.settlement_id);

      const poll = setInterval(async () => {
        try {
          const check = await axios.get(`${BACKEND_URL}/api/donations/admin/settlements`, { withCredentials: true });
          const found = (check.data.settlements || []).find(s => s.settlement_id === res.data.settlement_id);
          if (found && found.payment_status === 'paid') {
            clearInterval(poll);
            setPendingSettlementId(null);
            await Promise.all([fetchObligations(), fetchSettlements(), fetchOverview()]);
          }
        } catch (e) { /* keep polling while the admin completes checkout */ }
      }, 5000);
      setTimeout(() => clearInterval(poll), 300000);
    } catch (err) {
      console.error('Settlement failed:', err);
      setSettlementError(err.response?.data?.detail || 'Could not start the settlement');
    } finally {
      setSettlementProcessing(false);
      setSettlingCharity(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'transactions', label: 'All Transactions', icon: FileText },
    { id: 'recurring', label: 'Monthly Giving', icon: Repeat },
    { id: 'matching', label: 'Match Obligations', icon: Handshake },
    { id: 'settlements', label: 'Settlement History', icon: CheckCircle2 },
  ];

  return (
    <div className="space-y-6" data-testid="donations-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Donation Management</h1>
          <p className="text-sm text-gray-500 mt-1">Track donations, match obligations, and settlements</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} data-testid="export-csv-btn"
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all flex items-center gap-2">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => { fetchOverview(); fetchDonations(); fetchObligations(); fetchSettlements(); }}
            className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-all">
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Disbursement + charity claim nudges */}
      {staleDisbursements?.count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="stale-disbursement-banner">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-amber-900 text-sm">
                  {staleDisbursements.count} donation{staleDisbursements.count !== 1 ? 's' : ''} awaiting charity confirmation
                </p>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                  ${staleDisbursements.total_amount.toLocaleString()} was paid over {staleDisbursements.days} days ago and still
                  shows as “held for granting” on donor receipts. Confirm once Every.org has disbursed to the charity.
                </p>
              </div>
            </div>
            <button onClick={confirmStaleDisbursements} disabled={confirmingDisbursement}
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-xs font-bold px-4 py-2.5 rounded-lg transition-colors"
              data-testid="confirm-disbursements-btn">
              {confirmingDisbursement ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Mark as received by charity
            </button>
          </div>
        </div>
      )}

      {unclaimedCharities.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5" data-testid="unclaimed-charities-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-blue-900 text-sm">
                {unclaimedCharities.length} charit{unclaimedCharities.length !== 1 ? 'ies have' : 'y has'}n't claimed their Every.org profile
              </p>
              <p className="text-xs text-blue-800 mt-1 leading-relaxed">
                Gifts still reach them, but Every.org has to disburse by grant or cheque, which takes longer and can carry a
                1.5% disbursement fee. Nudge them to claim their profile to get fee-free weekly bank transfers.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {unclaimedCharities.slice(0, 6).map(c => {
                  const invited = outreach.find(o => o.charity_ein === c.charity_ein);
                  return (
                    <div key={c.charity_ein} className="flex items-center gap-1.5 bg-white border border-blue-200 rounded-lg pl-2.5 pr-1 py-1">
                      <a href={c.profile_url} target="_blank" rel="noreferrer"
                        className="text-xs font-semibold text-blue-700 hover:underline"
                        data-testid={`unclaimed-charity-${c.charity_ein}`}>
                        {c.charity_name} ↗
                      </a>
                      {invited ? (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-1 rounded"
                          data-testid={`invited-badge-${c.charity_ein}`}>
                          Invited {new Date(invited.sent_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <button onClick={() => { setClaimTarget(c); setClaimError(''); }}
                          data-testid={`invite-claim-btn-${c.charity_ein}`}
                          className="flex items-center gap-1 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded transition-colors">
                          <Mail className="w-3 h-3" /> Invite to claim
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {claimTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="claim-invite-modal">
          <form onSubmit={sendClaimInvite} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Invite {claimTarget.charity_name} to claim</h3>
                <p className="text-xs text-gray-500 mt-1">
                  We'll email them a prefilled invite explaining that your team's gifts are waiting and how to claim
                  their Every.org profile for fee-free bank transfers. Replies come to you.
                </p>
              </div>
              <button type="button" onClick={() => setClaimTarget(null)} data-testid="close-claim-modal"
                className="p-1 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Charity contact email</label>
              <input type="email" required value={claimEmail} onChange={e => setClaimEmail(e.target.value)}
                placeholder="info@charity.org" data-testid="claim-email-input"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm" />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Contact name (optional)</label>
              <input type="text" value={claimContact} onChange={e => setClaimContact(e.target.value)}
                placeholder="Development team" data-testid="claim-contact-input"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm" />
            </div>
            {claimError && <p className="text-xs text-red-600" data-testid="claim-error">{claimError}</p>}
            <button type="submit" disabled={sendingClaim} data-testid="send-claim-invite-btn"
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2">
              {sendingClaim ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send claim invite
            </button>
          </form>
        </div>
      )}

      {pendingSettlementId && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 flex items-center gap-3" data-testid="pending-settlement-banner">
          <Loader2 className="w-4 h-4 text-indigo-600 animate-spin" />
          <p className="text-xs text-indigo-900">
            Finish the match settlement in the Every.org tab — this page updates as soon as their confirmation arrives.
          </p>
        </div>
      )}

      {settlementError && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-xs text-red-800" data-testid="settlement-error">
          {settlementError}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl" data-testid="donations-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}>
              <Icon className="w-4 h-4" /> {tab.label}
            </button>
          );
        })}
      </div>

      {/* ─── OVERVIEW TAB ─── */}
      {activeTab === 'overview' && overview && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={DollarSign} label="Total Collected" value={`$${overview.total_collected.toLocaleString()}`}
              subtext={`${overview.donation_count} donation${overview.donation_count !== 1 ? 's' : ''}`}
              gradient="from-emerald-500 to-teal-600" />
            <StatCard icon={Handshake} label="Match Owed" value={`$${overview.total_match_owed.toLocaleString()}`}
              subtext="Employer match obligations"
              gradient="from-purple-500 to-indigo-600" />
            <StatCard icon={CheckCircle2} label="Match Settled" value={`$${overview.total_match_settled.toLocaleString()}`}
              subtext="Paid to charities"
              gradient="from-blue-500 to-cyan-600" />
            <StatCard icon={Clock} label="Pending Settlement" value={`$${overview.match_pending.toLocaleString()}`}
              subtext="Match still owed to charities"
              gradient="from-amber-500 to-orange-600" />
          </div>

          {/* Money Flow Diagram */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-sm font-bold text-gray-800 mb-4 uppercase tracking-wider">Donation Flow</h3>
            <div className="flex items-center justify-between max-w-3xl mx-auto">
              <div className="text-center">
                <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-2">
                  <Users className="w-7 h-7 text-blue-600" />
                </div>
                <p className="text-xs font-bold text-gray-800">Donors</p>
                <p className="text-[11px] text-gray-500">{overview.donation_count} donations</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
              <div className="text-center">
                <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center mx-auto mb-2">
                  <Shield className="w-7 h-7 text-indigo-600" />
                </div>
                <p className="text-xs font-bold text-gray-800">Every.org</p>
                <p className="text-[11px] text-gray-500">Processes & receipts</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
              <div className="text-center">
                <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                  <DollarSign className="w-7 h-7 text-emerald-600" />
                </div>
                <p className="text-xs font-bold text-gray-800">Collected</p>
                <p className="text-[11px] text-emerald-600 font-semibold">${overview.total_collected.toLocaleString()}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
              <div className="text-center">
                <div className="w-14 h-14 rounded-xl bg-teal-100 flex items-center justify-center mx-auto mb-2">
                  <Building2 className="w-7 h-7 text-teal-600" />
                </div>
                <p className="text-xs font-bold text-gray-800">Charities</p>
                <p className="text-[11px] text-gray-500">{overview.by_charity.length} recipient{overview.by_charity.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
            {overview.total_match_owed > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-3">
                <div className="px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg text-xs font-bold text-purple-700 flex items-center gap-1.5">
                  <Handshake className="w-3.5 h-3.5" /> + ${overview.total_match_owed.toLocaleString()} employer match
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300" />
                <div className="px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-bold text-emerald-700">
                  ${(overview.total_collected + overview.total_match_owed).toLocaleString()} total impact
                </div>
              </div>
            )}
          </div>

          {/* By Charity */}
          {overview.by_charity.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">By Charity</h3>
              </div>
              <div className="divide-y divide-gray-50">
                {overview.by_charity.map((c, i) => (
                  <div key={i} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{c.charity_name}</p>
                        <p className="text-xs text-gray-500">EIN: {c.charity_ein} &middot; {c.donation_count} donation{c.donation_count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">${c.total_donated.toLocaleString()}</p>
                      {c.total_match > 0 && (
                        <p className="text-xs text-purple-600 font-semibold">+ ${c.total_match.toLocaleString()} match</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TRANSACTIONS TAB ─── */}
      {activeTab === 'transactions' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400" />
              <input type="text" placeholder="Search charity..." value={searchCharity}
                onChange={e => setSearchCharity(e.target.value)}
                data-testid="search-charity-input"
                className="flex-1 text-sm border-0 focus:ring-0 p-0 placeholder-gray-400" />
            </div>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              data-testid="filter-status"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
              <option value="">All Status</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
            <select value={filterDonorType} onChange={e => setFilterDonorType(e.target.value)}
              data-testid="filter-donor-type"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
              <option value="">All Donors</option>
              <option value="employee">Employees</option>
              <option value="guest">Guests</option>
            </select>
            <span className="text-xs text-gray-500">{donationTotal} total</span>
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="donations-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Donor</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Charity</th>
                    <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Match</th>
                    <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Payment</th>
                    <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Disbursement</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {donations.map((d, i) => {
                    const payStatus = STATUS_CONFIG[d.payment_status] || STATUS_CONFIG.pending;
                    const disbStatus = DISBURSEMENT_CONFIG[d.disbursement_status] || DISBURSEMENT_CONFIG.awaiting_payment;
                    const PayIcon = payStatus.icon;
                    return (
                      <tr key={d.donation_id || i} className="hover:bg-gray-50 transition-colors" data-testid={`donation-row-${i}`}>
                        <td className="px-4 py-3 text-sm text-gray-600">{d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}</td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-900">{d.donor_name || 'Anonymous'}</p>
                          <p className="text-xs text-gray-500">{d.donor_email} &middot;
                            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${d.donor_type === 'employee' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                              {d.donor_type || 'guest'}
                            </span>
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{d.charity_name}</p>
                          {d.charity_ein && <p className="text-xs text-gray-400">EIN: {d.charity_ein}</p>}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">${d.amount?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-sm">
                          {d.employer_match_amount > 0 ? (
                            <span className="font-bold text-purple-600">${d.employer_match_amount.toLocaleString()}</span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${payStatus.color}`}>
                            <PayIcon className="w-3 h-3" /> {payStatus.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${disbStatus.color}`}>
                            {disbStatus.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {donations.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-400">No donations found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {donationPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-500">Page {donationPage} of {donationPages}</p>
                <div className="flex gap-2">
                  <button disabled={donationPage <= 1} onClick={() => fetchDonations(donationPage - 1)}
                    className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                    Previous
                  </button>
                  <button disabled={donationPage >= donationPages} onClick={() => fetchDonations(donationPage + 1)}
                    className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── MONTHLY GIVING TAB ─── */}
      {activeTab === 'recurring' && recurring && (
        <div className="space-y-4" data-testid="recurring-tab-content">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Repeat} label="Active Monthly Givers" value={recurring.active_count}
              subtext={recurring.lapsed_count ? `${recurring.lapsed_count} lapsed` : 'All renewing on time'}
              gradient="from-indigo-500 to-blue-600" />
            <StatCard icon={DollarSign} label="Expected Each Month" value={`$${recurring.expected_monthly_total.toLocaleString()}`}
              subtext={`$${recurring.expected_annual_total.toLocaleString()} a year`}
              gradient="from-emerald-500 to-teal-600" />
            <StatCard icon={TrendingUp} label="Average Gift" value={`$${recurring.average_monthly_gift.toLocaleString()}`}
              subtext="Per giver, per month"
              gradient="from-purple-500 to-indigo-600" />
            <StatCard icon={CheckCircle2} label="Given To Date" value={`$${recurring.given_to_date.toLocaleString()}`}
              subtext="From monthly gifts"
              gradient="from-amber-500 to-orange-600" />
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Monthly givers</h3>
              <p className="text-xs text-gray-400">Renewals are charged and receipted by Every.org</p>
            </div>
            <table className="w-full" data-testid="recurring-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Donor</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Charity</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase">Monthly</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Months</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase">Given</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Last charge</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recurring.givers.map((g, i) => (
                  <tr key={g.donation_id} className="hover:bg-gray-50" data-testid={`recurring-row-${i}`}>
                    <td className="px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">{g.donor_name}</p>
                      <p className="text-xs text-gray-500">{g.donor_email}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{g.charity_name}</td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">${g.monthly_amount.toLocaleString()}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-600">{g.months_given}</td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-emerald-700">${g.given_to_date.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {g.last_charge_at ? new Date(g.last_charge_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${
                        g.state === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        g.state === 'lapsed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {g.state === 'active' ? 'Active' : g.state === 'lapsed' ? 'Lapsed' : 'Awaiting first gift'}
                      </span>
                    </td>
                  </tr>
                ))}
                {recurring.givers.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center">
                    <Repeat className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">No monthly gifts yet</p>
                    <p className="text-xs text-gray-400 mt-1">Donors can choose “Monthly” when they give to any campaign</p>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── MATCH OBLIGATIONS TAB ─── */}
      {activeTab === 'matching' && obligations && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <p className="text-xs font-bold text-purple-600 uppercase tracking-wider">Total Match Owed</p>
              <p className="text-2xl font-black text-purple-800 mt-1">${obligations.summary.total_match_owed.toLocaleString()}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">Total Settled</p>
              <p className="text-2xl font-black text-emerald-800 mt-1">${obligations.summary.total_settled.toLocaleString()}</p>
            </div>
            <div className={`${obligations.summary.balance_due > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'} border rounded-xl p-4`}>
              <p className={`text-xs font-bold uppercase tracking-wider ${obligations.summary.balance_due > 0 ? 'text-amber-600' : 'text-gray-500'}`}>Balance Due</p>
              <p className={`text-2xl font-black mt-1 ${obligations.summary.balance_due > 0 ? 'text-amber-800' : 'text-gray-400'}`}>
                ${obligations.summary.balance_due.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Obligations list */}
          <div className="space-y-3">
            {obligations.obligations.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
                <Handshake className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No match obligations yet</p>
                <p className="text-xs text-gray-400 mt-1">When employees donate to matched campaigns, obligations appear here</p>
              </div>
            ) : (
              obligations.obligations.map((o, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`obligation-${i}`}>
                  <div className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedObligation(expandedObligation === i ? null : i)}>
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-xl bg-purple-100 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{o.charity_name}</p>
                        <p className="text-xs text-gray-500">EIN: {o.charity_ein} &middot; {o.donor_count} donor{o.donor_count !== 1 ? 's' : ''} &middot; {o.donation_count} donation{o.donation_count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-black text-gray-900">${o.balance_due.toLocaleString()}</p>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                          o.status === 'settled' ? 'bg-emerald-100 text-emerald-700' :
                          o.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>{o.status}</span>
                      </div>
                      {o.balance_due > 0 && (
                        <button onClick={(e) => { e.stopPropagation(); handleSettleMatch(o); }}
                          disabled={settlementProcessing}
                          data-testid={`settle-btn-${i}`}
                          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-xs font-bold hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 flex items-center gap-1.5">
                          {settlingCharity === o.charity_name ? (
                            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Settling...</>
                          ) : (
                            <><CreditCard className="w-3.5 h-3.5" /> Settle Match</>
                          )}
                        </button>
                      )}
                      {expandedObligation === i ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>
                  {expandedObligation === i && (
                    <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-500">Employee Donations</p>
                          <p className="text-lg font-bold text-gray-900">${o.total_employee_donated.toLocaleString()}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-purple-600">Match Owed</p>
                          <p className="text-lg font-bold text-purple-800">${o.total_match_owed.toLocaleString()}</p>
                        </div>
                        <div className="bg-emerald-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-emerald-600">Already Settled</p>
                          <p className="text-lg font-bold text-emerald-800">${o.total_settled.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                        <ArrowRight className="w-3 h-3" />
                        <span>Employees donate → Company matches at configured ratio → Company settles match to charity</span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ─── SETTLEMENT HISTORY TAB ─── */}
      {activeTab === 'settlements' && (
        <div className="space-y-4">
          {settlements.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No settlements yet</p>
              <p className="text-xs text-gray-400 mt-1">Settlements appear here after you pay employer match obligations</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full" data-testid="settlements-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Charity</th>
                    <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase">Amount</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Settled By</th>
                    <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Reference</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {settlements.map((s, i) => (
                    <tr key={s.settlement_id || i} className="hover:bg-gray-50" data-testid={`settlement-row-${i}`}>
                      <td className="px-4 py-3 text-sm text-gray-600">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{s.charity_name}</p>
                        {s.charity_ein && <p className="text-xs text-gray-400">EIN: {s.charity_ein}</p>}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">${s.amount?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{s.settled_by_name || s.settled_by}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold ${
                          s.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {s.status === 'completed' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          {s.status === 'completed' ? 'Settled' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 font-mono">{s.settlement_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DonationsView;
